/**
 * 分析页 — 合并原「趋势分析」和「竞品 360」的图表功能
 */
import { useEffect, useMemo, useState } from 'react';
import { BarChart3 } from 'lucide-react';

import { PageShell } from '../components/PageShell';
import { TrendChart } from '../components/TrendChart';
import { TrendFilterBar } from '../components/TrendFilterBar';
import { usePriceIntelStore } from '../stores/price-intel-store';
import type { PriceIntelPriceFact, PriceIntelTrendResponse } from '../types';

// ---------------------------------------------------------------------------
// 趋势数据聚合
// ---------------------------------------------------------------------------
function aggregateTrends(
  facts: PriceIntelPriceFact[],
  filters: { competitor?: string; region?: string; country?: string },
): PriceIntelTrendResponse {
  const map = new Map<string, Map<string, { sum: number; count: number }>>();
  for (const f of facts) {
    if (filters.competitor && f.competitor !== filters.competitor) continue;
    if (filters.region && f.region !== filters.region) continue;
    if (filters.country && f.country !== filters.country) continue;
    if (!f.competitor || !f.year_quarter || f.price_per_sqm == null) continue;

    const compMap = map.get(f.competitor) ?? new Map();
    map.set(f.competitor, compMap);
    const cur = compMap.get(f.year_quarter) ?? { sum: 0, count: 0 };
    cur.sum += f.price_per_sqm;
    cur.count += 1;
    compMap.set(f.year_quarter, cur);
  }

  const series = Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([competitor, points]) => ({
      competitor,
      points: Array.from(points.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([year_quarter, agg]) => ({
          year_quarter,
          avg_price_per_sqm: Math.round(agg.sum / agg.count),
          fact_count: agg.count,
        })),
    }));

  return { series, filters };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const mutedText: React.CSSProperties = { color: 'var(--color-system-secondary)' };

export function IntelAnalyticsPage() {
  const { facts, fetchFacts, productSpecs } = usePriceIntelStore();
  const [competitor, setCompetitor] = useState('');
  const [region, setRegion] = useState('');
  const [country, setCountry] = useState('');

  useEffect(() => {
    if (facts.length === 0) void fetchFacts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const competitorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const f of facts) if (f.competitor) set.add(f.competitor);
    for (const s of productSpecs) if (s.competitor) set.add(s.competitor);
    return Array.from(set).sort();
  }, [facts, productSpecs]);

  const regionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const f of facts) {
      if (competitor && f.competitor !== competitor) continue;
      if (f.region) set.add(f.region);
    }
    return Array.from(set).sort();
  }, [facts, competitor]);

  const countryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const f of facts) {
      if (competitor && f.competitor !== competitor) continue;
      if (region && f.region !== region) continue;
      if (f.country) set.add(f.country);
    }
    return Array.from(set).sort();
  }, [facts, competitor, region]);

  const trends = useMemo(
    () =>
      aggregateTrends(facts, {
        competitor: competitor || undefined,
        region: region || undefined,
        country: country || undefined,
      }),
    [facts, competitor, region, country],
  );

  return (
    <PageShell>
      <div className="space-y-4">
        <div>
          <h1
            className="flex items-center gap-2 text-xl font-semibold"
            style={{ color: 'var(--color-system-primary)' }}
          >
            <BarChart3 size={20} style={{ color: 'var(--color-key)' }} />
            分析
          </h1>
          <p className="mt-1 text-sm" style={mutedText}>
            基于已录入数据的价格趋势和竞品对比可视化。
          </p>
        </div>

        <TrendFilterBar
          competitorOptions={competitorOptions}
          regionOptions={regionOptions}
          countryOptions={countryOptions}
          competitor={competitor}
          region={region}
          country={country}
          onCompetitorChange={(v) => { setCompetitor(v); setRegion(''); setCountry(''); }}
          onRegionChange={(v) => { setRegion(v); setCountry(''); }}
          onCountryChange={setCountry}
        />

        <TrendChart data={trends} />

        {trends.series.length === 0 && (
          <div
            className="rounded-xl border border-dashed px-6 py-12 text-center text-sm"
            style={{ borderColor: 'var(--color-system-quaternary)', ...mutedText }}
          >
            暂无趋势数据。在「录入」页面添加包含价格和季度信息的竞品数据后，这里会自动生成可视化图表。
          </div>
        )}
      </div>
    </PageShell>
  );
}
